import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { X } from '@phosphor-icons/react';
import { PhoneFrame } from '../PhoneFrame';
import { FormatToggle } from './FormatToggle';
import type { Format } from '../../types/snak';
interface OverlayProps {
  eyebrow: string;
  format: Format;
  onFormatChange: (format: Format) => void;
  onClose: () => void;
  /** In phone format, present the content inside the device. Defaults to true. */
  frame?: boolean;
  /** Attached to the desktop-format scroll container, for scroll tracking. */
  bodyRef?: React.RefObject<HTMLDivElement>;
  children: React.ReactNode;
}

/** Full-page reading surface for the explanation layers. */
export function Overlay({
  eyebrow,
  format,
  onFormatChange,
  onClose,
  frame = true,
  bodyRef,
  children
}: OverlayProps) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = previous;
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);
  const framed = frame && format === 'mobile';
  const content = <motion.div initial={{
    opacity: 0,
    y: 16
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.5,
    delay: 0.08,
    ease: [0.22, 1, 0.36, 1]
  }} data-id="element-205" data-mp="2ggqstczh1kg">
      {children}
    </motion.div>;
  return <motion.div role="dialog" aria-modal="true" aria-label={eyebrow} initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.3,
    ease: [0.22, 1, 0.36, 1]
  }} className="fixed inset-0 z-50 flex flex-col bg-[#F7F5F0] font-body" data-id="element-206" data-mp="v4mdvmrif2ng">
      <div className="flex shrink-0 items-center justify-between gap-3 border-b border-[rgba(1,51,43,0.1)] px-5 py-3 lg:px-10" data-id="element-207" data-mp="1ivs1io3oyz8b">
        <span className="truncate font-body text-[10px] uppercase tracking-[0.24em] text-[rgba(1,51,43,0.45)]" data-id="element-208" data-mp="1e2p8ch2ttrsn">
          {eyebrow}
        </span>
        <div className="flex shrink-0 items-center gap-2" data-id="element-209" data-mp="21zv7h74g9zpv">
          <FormatToggle value={format} onChange={onFormatChange} data-id="element-210" data-mp="221g20wqrlinq" />
          <button type="button" onClick={onClose} aria-label="Close" className="inline-flex h-9 items-center gap-2 rounded-full border border-[rgba(1,51,43,0.18)] px-4 font-body text-[10px] uppercase tracking-[0.16em] text-background transition-colors hover:border-[rgba(1,51,43,0.5)] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary" data-id="element-211" data-mp="1o75l31l0ess8">
            <X size={13} weight="bold" aria-hidden="true" data-id="element-212" data-mp="15anp27z4o6q6" />
            <span className="hidden sm:inline" data-id="element-213" data-mp="19a4vb3mdp7we">Close</span>
          </button>
        </div>
      </div>

      {framed ? <div className="flex flex-1 items-center justify-center overflow-hidden px-4 py-5" data-id="element-214" data-mp="2zof5bqbypoul">
          <PhoneFrame label={`${eyebrow} in phone format`} sizeClassName="h-[clamp(500px,calc(100vh-136px),860px)] w-auto aspect-[344/688]" data-id="element-215" data-mp="33nnyasz6hip3">
            {content}
          </PhoneFrame>
        </div> : <div ref={bodyRef} className="flex-1 overflow-y-auto" data-id="element-216" data-mp="nxxh6xfji4r4">
          {content}
        </div>}
    </motion.div>;
}